import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import EToast from '@/components/everridge/EToast';
import { CreateNotificationRequestDto, NotificationDetailsResponseDto } from '@/features/notifications/types/notifications-dto';
import { t } from 'i18next';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {} & ApiHookProps;

export const useNotificationDetailsApi = ({ id }: Props = {}) => {
    const queryClient = useQueryClient();
    const axiosInstance = useAxiosAuth();

    const {
        data: notificationDetailsData, 
        isLoading: isLoadingNotificationDetailsData, 
        isError: isErrorNotificationDetailsData,       
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.notifications.queryKeys.details, 
            id
        ],
        queryFn: async (): Promise<NotificationDetailsResponseDto> => {
            const url = `${ApiConfig.queries.features.notifications.endpoints.get}/${id}`; 
            const response = await axiosInstance.get<NotificationDetailsResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
        enabled: !!id,
    });

    const {
        mutateAsync: createNotification, 
        isPending: isCreatingNotification, 
        isError: isErrorCreatingNotification
    } = useMutation({
        mutationFn: async (dto: CreateNotificationRequestDto) => {
            const response = await axiosInstance.post(
                ApiConfig.queries.features.notifications.endpoints.create, dto
            );  
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ApiConfig.queries.features.notifications.queryKeys.adminList] 
            });
            EToast({
                variant: 'success',
                message: t('main.notifications.toasts.createPost.success.message'), 
                description: t('main.notifications.toasts.createPost.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.notifications.toasts.createPost.error.message'), 
                description: t('main.notifications.toasts.createPost.error.message')
            });
        }
    });

    const {
        mutateAsync: readNotification, 
        isPending: isReadingNotification, 
        isError: isErrorReadingNotification
    } = useMutation({
        mutationFn: async (id: string) => {
            const url = `${ApiConfig.queries.features.notifications.endpoints.update}/${id}`; 
            const response = await axiosInstance.patch(url);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ApiConfig.queries.features.notifications.queryKeys.ownerList]
            });           
        },
        onError: () => {}
    });
    
    const {
        mutateAsync: readAllNotification, 
        isPending: isReadingAllNotification, 
        isError: isErrorReadingAllNotification
    } = useMutation({
        mutationFn: async () => {
            const url = ApiConfig.queries.features.notifications.endpoints.bulkMarkAsRead; 
            const response = await axiosInstance.post(url);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ApiConfig.queries.features.notifications.queryKeys.ownerList]
            });           
        },
        onError: () => {}
    }); 

    return {
        notificationDetailsData,
        isLoadingNotificationDetailsData,
        isErrorNotificationDetailsData,
        createNotification, 
        isCreatingNotification, 
        isErrorCreatingNotification,
        readNotification, 
        isReadingNotification, 
        isErrorReadingNotification,
        readAllNotification,
        isReadingAllNotification,
        isErrorReadingAllNotification
    }
};
